import { DEFAULT_ZOOM_LEVEL, MAP_ZOOM_LEVEL, ZOOM_LEVEL_STEP } from '@/constants/floor-guide';
import type { DabeeoMap } from 'dabeeomaps';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMapControl } from './useMapControl';

export const useMapZoomLevel = (currentMap?: DabeeoMap) => {
    const [zoomLevel, setZoomLevel] = useState<number>(currentMap?.control?.getCurrentZoomLevel?.() ?? DEFAULT_ZOOM_LEVEL);

    const { handleChangeZoom } = useMapControl(currentMap);

    const handleZoomIn = useCallback(() => {
        setZoomLevel((prev) => {
            const next = Math.min((currentMap?.control?.getCurrentZoomLevel?.() || prev) + ZOOM_LEVEL_STEP, MAP_ZOOM_LEVEL.MAX);
            if (next !== prev) {
                handleChangeZoom({ zoom: next });
            }
            return next;
        });
    }, [handleChangeZoom, currentMap]);

    const handleZoomOut = useCallback(() => {
        setZoomLevel((prev) => {
            const next = Math.max((currentMap?.control?.getCurrentZoomLevel?.() || prev) - ZOOM_LEVEL_STEP, MAP_ZOOM_LEVEL.MIN);
            if (next !== prev) {
                handleChangeZoom({ zoom: next });
            }
            return next;
        });
    }, [handleChangeZoom, currentMap]);

    const isDisabledZoomIn = useMemo(() => {
        return zoomLevel >= MAP_ZOOM_LEVEL.MAX;
    }, [zoomLevel]);

    const isDisabledZoomOut = useMemo(() => {
        return zoomLevel <= MAP_ZOOM_LEVEL.MIN;
    }, [zoomLevel]);

    useEffect(() => {
        if (!currentMap) {
            return;
        }

        setZoomLevel(currentMap?.control?.getCurrentZoomLevel?.() ?? DEFAULT_ZOOM_LEVEL);
    }, [currentMap]);

    return {
        zoomLevel,
        handleZoomIn,
        handleZoomOut,
        setZoomLevel,
        isDisabledZoomIn,
        isDisabledZoomOut,
    };
};
